import React, { useState, useMemo } from 'react';
import { categoryApi } from '../../category/api/categoryApi';
import { Dropdown, Message } from 'semantic-ui-react';
import { PageSection } from 'common/styles/page';

import { SurveystructureComplianceRead } from 'store/generated/admin/category';
import { useNavigate } from 'react-router';
import CategoryList from '../../category/components/CategoryList';
import { StyledAddButton } from 'common/components/button/StyledButton';
import LoadingSpinner from '@/common/components/LoadingSpinner';

interface OrganizationCategoriesProps {
    organizationId: number;
}
const OrganizationCategories: React.FC<OrganizationCategoriesProps> = ({ organizationId }) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [statusFilter, setStatusFilter] = useState<'active' | 'archived'>('active');

    const {
        data: categories = [],
        isLoading,
        error,
        refetch,
    } = categoryApi.useApiAdminV1CategoryListQuery({ status: statusFilter === 'active' ? 1 : 2 });

    const [updateCategory] = categoryApi.useApiAdminV1CategoryPartialUpdateMutation();
    const navigate = useNavigate();

    const hasPrimaryCategory = categories.filter(cat => cat.organization === organizationId).some(cat => cat.priority === 1);

    const handleArchiveToggle = async (category: SurveystructureComplianceRead) => {
        const isArchived = category.status === 2;
        try {
            await updateCategory({
                id: category.id,
                patchedSurveystructureCompliance: {
                    status: isArchived ? 1 : 2,
                },
            }).unwrap();
            refetch();
        } catch (err) {
            console.error(`Failed to ${isArchived ? 'unarchive' : 'archive'} category:`, err);
        }
    };

    const filteredCategories = useMemo(() => categories.filter(cat => {
        const matchesStatus =
            (statusFilter === 'active' && cat.status === 1) ||
            (statusFilter === 'archived' && cat.status === 2);
        const matchesOrganization = cat.organization === organizationId;
        return matchesStatus && matchesOrganization;
    }).sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0)), [categories, statusFilter, organizationId]);

    return (<PageSection >
        <PageSection display="flex" justifyContent="space-between" alignItems="center" padbottom="15px">
            <Dropdown
                placeholder="Filter by Status"
                selection
                value={statusFilter}
                options={[
                    { key: 'active', text: 'Active', value: 'active' },
                    { key: 'archived', text: 'Archived', value: 'archived' },
                ]}
                onChange={(_, data) => {
                    setStatusFilter(data.value as typeof statusFilter);
                    setCurrentPage(1);
                }}
                style={{
                    height: '36px',
                    border: '1px solid #ccc',
                    borderRadius: '5px',
                    fontSize: '14px',
                    minWidth: '200px',
                }}
            />

            <StyledAddButton
                label="New Category"
                width="150px"
                height="34px"
                onClick={() => {
                    navigate(`/settings/admin/categories/create?orgId=${organizationId}&priority=${hasPrimaryCategory ? 2 : 1}`);
                }}
            />

        </PageSection>

        <PageSection>
            {error && <Message negative content="Failed to load categories." />}
            {isLoading ? (
                <LoadingSpinner />
            ) : (
                <CategoryList
                    categories={filteredCategories}
                    currentPage={currentPage}
                    onPageChange={setCurrentPage}
                    onNavigate={navigate}
                    onArchiveToggle={handleArchiveToggle}
                    organizationId={organizationId}
                />
            )}

        </PageSection>
    </PageSection>
    );
};

export default OrganizationCategories;
